import React, { useState, useEffect, useRef } from "react";
import { api } from "../../lib/api";
import { usePortfolio } from "../../context/PortfolioContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { fmtINR, fmtUnits, longDate } from "../../lib/format";
import { Check, Pencil, Trash2, Upload, FileText, AlertCircle } from "lucide-react";

const DEFAULT_AMC_PCT = "0.125";

// The 7 auto-journal legs that fire when a subscription / redemption is recorded.
// Each leg has a stable backend key + a human label + a helper to compute its
// posting direction & amount based on (type, funds, amcFee). The labels show
// the SUBSCRIBE direction; redemption flips Dr↔Cr (backend handles the flip).
const JOURNAL_LEGS = [
  { key: "zerodha_capital",  sub: "Dr Zerodha Capital",  red: "Cr Zerodha Capital",  scale: "amount" },
  { key: "member_capital",   sub: "Cr Member Capital",   red: "Dr Member Capital",   scale: "amount" },
  { key: "bank_transaction", sub: "Dr Bank Transaction", red: "Cr Bank Transaction", scale: "gross" },
  { key: "member_units",     sub: "Dr Member Units",     red: "Cr Member Units",     scale: "units" },
  { key: "zerodha_units",    sub: "Cr Zerodha Units",    red: "Dr Zerodha Units",    scale: "units" },
  { key: "amc_asset",        sub: "Dr AMC (asset)",      red: "Cr AMC (asset)",      scale: "amc" },
  { key: "amc_expenses",     sub: "Cr AMC Expenses",     red: "Dr AMC Expenses",     scale: "amc" },
];
const ALL_LEG_KEYS = JOURNAL_LEGS.map((l) => l.key);

// Indian-locale number with INR symbol (used inside auto-generated Details).
function fmtMoney(n) {
  const v = Number(n);
  if (!isFinite(v)) return "";
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}
function fmtUnitsPlain(n) {
  const v = Number(n);
  if (!isFinite(v)) return "";
  // Keep up to 4 decimals but trim trailing zeros for readability
  return v.toFixed(4).replace(/\.?0+$/, "");
}

// Build the templated Details / Notes string for sub / red.
//   Subscription: "Received ₹X from <Member> on <Date> and issued <Units> at NAV ₹Y."
//   Redemption:   "Paid ₹X to <Member> on <Date> and redeemed <Units> at NAV ₹Y."
function buildAutoDetails({ type, funds, nav, units, memberName, date }) {
  if (type !== "subscription" && type !== "redemption") return "";
  const f = parseFloat(funds);
  const n = parseFloat(nav);
  const u = parseFloat(units);
  if (!memberName || !date || !isFinite(f) || f <= 0 || !isFinite(n) || n <= 0 || !isFinite(u) || u <= 0) {
    return "";
  }
  if (type === "subscription") {
    return `Received ${fmtMoney(f)} from ${memberName} on ${date} and issued ${fmtUnitsPlain(u)} units at NAV ₹${n.toFixed(4)}.`;
  }
  return `Paid ${fmtMoney(f)} to ${memberName} on ${date} and redeemed ${fmtUnitsPlain(u)} units at NAV ₹${n.toFixed(4)}.`;
}

const EMPTY = (nav) => ({
  date: new Date().toISOString().slice(0, 10),
  memberId: "",
  type: "subscription",
  details: "",
  funds: "",
  nav: nav ? nav.toFixed(4) : "",
  units: "",
  amcEnabled: true,
  amcRate: DEFAULT_AMC_PCT, // percent string, e.g. "0.125" = 0.125%
  _autoDetails: true,       // true until admin manually edits the Details field
  journalLegs: ALL_LEG_KEYS, // all 7 legs selected by default
});

export default function AddTransaction() {
  const { toast } = useToast();
  const { members, transactions, summary, reload } = usePortfolio();

  const [form, setForm] = useState(EMPTY(summary?.currentNAV));
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(null); // transaction being edited

  // Bulk upload state
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkReplaceAll, setBulkReplaceAll] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Auto-fill NAV input with current portfolio NAV once when summary loads
  const navAutofilledRef = useRef(false);
  useEffect(() => {
    if (!navAutofilledRef.current && summary?.currentNAV && !form.nav) {
      navAutofilledRef.current = true;
      setForm((f) => ({ ...f, nav: summary.currentNAV.toFixed(4) }));
    }
  }, [summary, form.nav]);

  const update = (setFn, allMembers) => (k, v) => {
    setFn((f) => {
      const next = { ...f, [k]: v };
      // If the user manually types in the details box, stop auto-filling.
      if (k === "details") {
        next._autoDetails = false;
      }
      const funds = parseFloat(next.funds);
      const nav = parseFloat(next.nav);
      const triggers = ["funds", "nav", "type", "amcEnabled", "amcRate"];
      if (!isNaN(funds) && !isNaN(nav) && nav > 0 && triggers.includes(k)) {
        if (next.type === "bonus") {
          // Bonus: no AMC fee. Units = funds / nav directly.
          next.units = (funds / nav).toFixed(4);
          next._amcFee = "0.00";
          next._netFunds = funds.toFixed(2);
        } else {
          // AMC Fee — optional, configurable %. Default 0.125%.
          //   Subscription: member PAYS gross, AMC is deducted → NET invested
          //                 Units_Cr = (gross − AMC) / NAV
          //   Redemption:  member RECEIVES gross, AMC is added on top → MORE units redeemed
          //                 Units_Dr = (gross + AMC) / NAV
          const ratePct = parseFloat(next.amcRate);
          const amcRate = next.amcEnabled && !isNaN(ratePct) && ratePct >= 0 ? ratePct / 100 : 0;
          const amcFee = funds * amcRate;
          const isRedemption = next.type === "redemption";
          const unitsFunds = isRedemption ? funds + amcFee : funds - amcFee;
          next.units = (unitsFunds / nav).toFixed(4);
          next._amcFee = amcFee.toFixed(2);
          next._netFunds = unitsFunds.toFixed(2);
        }
      }
      // Auto-fill Details / Notes for sub & red, only while the admin hasn't
      // manually edited the textarea. The string is rebuilt on every change so
      // it stays in sync as funds / NAV / member / date / type evolve.
      if (next._autoDetails && next.type !== "bonus") {
        const member = (allMembers || []).find((m) => m.id === next.memberId);
        const auto = buildAutoDetails({
          type: next.type,
          funds: next.funds,
          nav: next.nav,
          units: next.units,
          memberName: member?.name || "",
          date: next.date,
        });
        if (auto) next.details = auto;
      }
      // Switching to bonus: clear the auto sub/red sentence so the user can
      // write a bonus-specific note. Switching back resumes auto-fill.
      if (k === "type" && next.type === "bonus" && next._autoDetails) {
        next.details = "";
      }
      return next;
    });
  };
  const updateNew = update(setForm, members);
  const updateEdit = update(setEditing, members);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.memberId) return toast({ title: "Select a member" });
    const grossFunds = parseFloat(form.funds);
    const nav = parseFloat(form.nav);
    const units = parseFloat(form.units);
    if (isNaN(grossFunds) || grossFunds <= 0) return toast({ title: "Enter valid amount" });
    if (isNaN(nav) || nav <= 0) return toast({ title: "Enter valid NAV" });
    if (isNaN(units) || units <= 0) return toast({ title: "Enter valid units" });

    // AMC fee: optional, configurable rate. 0 for bonus or when disabled.
    //   Subscription: fundsCr stored = NET (gross − AMC)    → Units_Cr = NET / NAV
    //   Redemption:   fundsDr stored = GROSS (what member receives)
    //                 Units_Dr = (gross + AMC) / NAV (extra units redeemed to cover fee)
    const isBonus = form.type === "bonus";
    const isRedemption = form.type === "redemption";
    const ratePct = parseFloat(form.amcRate);
    const amcRate =
      isBonus || !form.amcEnabled || isNaN(ratePct) || ratePct < 0
        ? 0
        : ratePct / 100;
    const amcFee = +(grossFunds * amcRate).toFixed(2);
    // Amount recorded on the ledger for cash movement (funds in/out)
    const recordedFunds = isBonus
      ? grossFunds
      : isRedemption ? grossFunds : +(grossFunds - amcFee).toFixed(2);

    setBusy(true);
    try {
      await api.addTransaction({
        date: form.date,
        memberId: form.memberId,
        type: form.type,
        details: form.details,
        funds: recordedFunds,  // sub: net invested; red: gross payout to member; bonus: notional
        nav,
        units,
        amcFee,
        // Bonus doesn't auto-journal; for sub/red, send the selected leg keys.
        // Empty array → no legs post (rare); null → backend posts all (default).
        journalLegs: isBonus ? null : (form.journalLegs || []),
      });
      const verb = isBonus ? "Bonus" : (form.type === "subscription" ? "+" : "-");
      toast({
        title: "Transaction recorded",
        description: isBonus
          ? `Bonus ${fmtINR(recordedFunds)} · ${fmtUnits(units)} units`
          : `${verb}${fmtINR(recordedFunds)} ${isRedemption ? "paid out" : "net"} · AMC fee ${fmtINR(amcFee)} · ${fmtUnits(units)} units`,
      });
      setForm(EMPTY(summary?.currentNAV));
      await reload();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Could not save" });
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (t) => {
    const isCredit = t.type === "subscription" || t.type === "bonus" || (t.fundsCr || 0) > 0;
    setEditing({
      id: t.id,
      memberId: t.memberId,
      memberName: t.name,
      date: t.date,
      type: t.type,
      details: t.details || "",
      funds: String(isCredit ? t.fundsCr : t.fundsDr),
      units: String(isCredit ? t.unitsCr : t.unitsDr),
      nav: t.nav.toFixed(4),
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const funds = parseFloat(editing.funds);
    const nav = parseFloat(editing.nav);
    const units = parseFloat(editing.units);
    if (isNaN(funds) || funds <= 0) return toast({ title: "Invalid amount" });
    if (isNaN(nav) || nav <= 0) return toast({ title: "Invalid NAV" });
    if (isNaN(units) || units <= 0) return toast({ title: "Invalid units" });

    setBusy(true);
    try {
      await api.editTransaction(editing.id, {
        date: editing.date,
        type: editing.type,
        details: editing.details,
        funds,
        nav,
        units,
      });
      toast({ title: "Transaction updated" });
      setEditing(null);
      await reload();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Could not update" });
    } finally {
      setBusy(false);
    }
  };

  const deleteTx = async (t) => {
    if (!window.confirm(`Delete this ${t.type} for ${t.name}? This will recalculate balances.`)) return;
    try {
      await api.deleteTransaction(t.id);
      toast({ title: "Transaction deleted" });
      await reload();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Could not delete" });
    }
  };

  // ============= Bulk CSV upload =============
  const parseCsvLine = (line) => {
    // Tiny CSV parser supporting quoted fields with commas (e.g. "1,000.00")
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQ = false; }
        else cur += c;
      } else {
        if (c === '"') inQ = true;
        else if (c === ",") { out.push(cur); cur = ""; }
        else cur += c;
      }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };

  const handleBulkFile = async (file) => {
    setBulkFile(file);
    setBulkRows([]);
    setBulkErrors([]);
    if (!file) return;

    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) {
      setBulkErrors(["CSV must have a header row and at least one data row"]);
      return;
    }

    const header = parseCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, ""));
    const colIdx = (name) => header.indexOf(name);
    // New canonical schema: Date, Member(Name), Type, NAV, Units, Amount, AMC Fees
    // Backward-compatible with old schema (Funds instead of Amount)
    const required = ["date", "type", "nav"];
    const missing = required.filter((r) => colIdx(r) === -1);
    if (missing.length) {
      setBulkErrors([
        `Missing required columns: ${missing.join(", ")}`,
        "Expected: Date, Member, Type, NAV, Units, Amount, AMC Fees (Member may also be 'MemberName'; Amount may also be 'Funds')",
      ]);
      return;
    }
    // Member can be 'member' or 'membername'
    const memberCol = colIdx("member") >= 0 ? "member" : "membername";
    if (colIdx(memberCol) === -1) {
      setBulkErrors([`Missing required column: Member (or MemberName)`]);
      return;
    }
    // Amount can be 'amount' or 'funds'
    const amountCol = colIdx("amount") >= 0 ? "amount" : "funds";
    if (colIdx(amountCol) === -1) {
      setBulkErrors([`Missing required column: Amount (or Funds)`]);
      return;
    }

    const errs = [];
    const rows = [];
    for (let li = 1; li < lines.length; li++) {
      const cells = parseCsvLine(lines[li]);
      const get = (k) => (cells[colIdx(k)] || "").trim();
      const cleanNum = (v) => parseFloat(String(v).replace(/[,₹\s]/g, ""));
      const date = get("date");
      const memberName = get(memberCol);
      const type = get("type").toLowerCase();
      const funds = cleanNum(get(amountCol));
      const nav = cleanNum(get("nav"));
      const unitsRaw = get("units");
      const units = unitsRaw ? cleanNum(unitsRaw) : null;
      const amcRaw = colIdx("amcfees") >= 0 ? get("amcfees") : (colIdx("amcfee") >= 0 ? get("amcfee") : "");
      const amcFee = amcRaw ? cleanNum(amcRaw) : 0;
      const details = colIdx("details") >= 0 ? get("details") : "";

      if (!date) { errs.push(`Row ${li + 1}: missing date`); continue; }
      if (!memberName) { errs.push(`Row ${li + 1}: missing member name`); continue; }
      if (!["subscription", "redemption", "bonus"].includes(type)) {
        errs.push(`Row ${li + 1}: type must be 'subscription', 'redemption', or 'bonus' (got '${type}')`);
        continue;
      }
      if (isNaN(funds) || funds <= 0) { errs.push(`Row ${li + 1}: invalid amount '${get(amountCol)}'`); continue; }
      if (isNaN(nav) || nav <= 0) { errs.push(`Row ${li + 1}: invalid NAV '${get("nav")}'`); continue; }

      const m = members.find((mm) => mm.name.toLowerCase() === memberName.toLowerCase());
      if (!m) {
        errs.push(`Row ${li + 1}: unknown member '${memberName}' — must match an existing family member exactly`);
        continue;
      }

      rows.push({
        date,
        memberName: m.name,
        memberId: m.id,
        type,
        funds,
        nav,
        units: units && units > 0 ? units : funds / nav,
        amcFee: isNaN(amcFee) ? 0 : amcFee,
        details,
      });
    }
    setBulkRows(rows);
    setBulkErrors(errs);
  };

  const submitBulk = async () => {
    if (bulkRows.length === 0) return toast({ title: "No valid rows to upload" });
    if (bulkErrors.length > 0) return toast({ title: "Fix validation errors first" });
    if (bulkReplaceAll && !window.confirm(
      `This will DELETE ALL ${transactions.length} existing transactions and replace them with ${bulkRows.length} new ones from the CSV. This cannot be undone. Continue?`
    )) return;

    setBulkBusy(true);
    try {
      const res = await api.bulkTransactions(bulkRows, bulkReplaceAll);
      toast({
        title: "Upload successful",
        description: `${res.inserted} transactions ${bulkReplaceAll ? "replaced" : "added"}. Balances + NAV recomputed.`,
      });
      setBulkFile(null);
      setBulkRows([]);
      setBulkErrors([]);
      setBulkReplaceAll(false);
      await reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errs = typeof detail === "object" ? detail.errors || [detail.message] : [detail || "Upload failed"];
      setBulkErrors(errs);
      toast({ title: "Upload failed", description: errs[0] });
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadTemplate = () => {
    const csv = [
      "Date,Member,Type,NAV,Units,Amount,AMC Fees,Details",
      "2026-06-08,Habiba Jetha,subscription,103.5641,1928.7572,199750,250,Received 2,00,000",
      "2026-06-08,Moshin Chamadia,redemption,103.5641,500,51782.05,0,Partial redemption",
      "2026-04-01,Mehrunissa Jetha,bonus,100,30.9738,3097.38,0,Bonus FY 2025-2026",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cashflow_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ============= Bulk NAV History upload =============
  const [navBulkFile, setNavBulkFile] = useState(null);
  const [navBulkRows, setNavBulkRows] = useState([]);
  const [navBulkErrors, setNavBulkErrors] = useState([]);
  const [navBulkReplace, setNavBulkReplace] = useState(false);
  const [navBulkBusy, setNavBulkBusy] = useState(false);

  const handleNavBulkFile = async (file) => {
    setNavBulkFile(file);
    setNavBulkRows([]);
    setNavBulkErrors([]);
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) {
      setNavBulkErrors(["CSV must have a header row and at least one data row"]);
      return;
    }
    const header = parseCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, ""));
    const idx = (n) => header.indexOf(n);
    if (idx("date") === -1 || idx("nav") === -1 || idx("type") === -1) {
      setNavBulkErrors(["Missing required columns. Expected: Date, NAV, Member, Type (subscription/redemption/bonus/daily), Funds, Units"]);
      return;
    }
    const memCol = idx("member") >= 0 ? "member" : "membername";
    const errs = [];
    const rows = [];
    for (let li = 1; li < lines.length; li++) {
      const cells = parseCsvLine(lines[li]);
      const get = (k) => (cells[idx(k)] || "").trim();
      const clean = (v) => parseFloat(String(v).replace(/[,₹\s]/g, ""));
      const date = get("date");
      const nav = clean(get("nav"));
      const type = get("type").toLowerCase() || "daily";
      const memberName = idx(memCol) >= 0 ? get(memCol) : "";
      const funds = idx("funds") >= 0 ? clean(get("funds")) : 0;
      const units = idx("units") >= 0 ? clean(get("units")) : 0;
      if (!date) { errs.push(`Row ${li + 1}: missing date`); continue; }
      if (isNaN(nav) || nav <= 0) { errs.push(`Row ${li + 1}: invalid NAV '${get("nav")}'`); continue; }
      if (!["subscription", "redemption", "bonus", "daily", "live"].includes(type)) {
        errs.push(`Row ${li + 1}: type must be subscription/redemption/bonus/daily/live (got '${type}')`);
        continue;
      }
      let memberId = null;
      if (["subscription", "redemption", "bonus"].includes(type)) {
        if (!memberName) { errs.push(`Row ${li + 1}: ${type} row needs Member`); continue; }
        const m = members.find((mm) => mm.name.toLowerCase() === memberName.toLowerCase());
        if (!m) { errs.push(`Row ${li + 1}: unknown member '${memberName}'`); continue; }
        memberId = m.id;
        if ((!funds || funds <= 0) && (!units || units <= 0)) {
          errs.push(`Row ${li + 1}: ${type} row needs Funds or Units`); continue;
        }
      }
      rows.push({
        date,
        nav,
        type,
        memberName: memberName || null,
        memberId,
        funds: funds > 0 ? funds : (units > 0 ? units * nav : 0),
        units: units > 0 ? units : (funds > 0 ? funds / nav : 0),
      });
    }
    setNavBulkRows(rows);
    setNavBulkErrors(errs);
  };

  const submitNavBulk = async () => {
    if (navBulkRows.length === 0) return toast({ title: "No valid rows" });
    if (navBulkErrors.length > 0) return toast({ title: "Fix validation errors first" });
    if (navBulkReplace && !window.confirm(
      "This will DELETE all existing daily/live NAV snapshots before inserting the CSV's daily entries. Subscription/redemption/bonus transactions are NOT touched. Continue?"
    )) return;
    setNavBulkBusy(true);
    try {
      const res = await api.bulkNavHistory(navBulkRows, navBulkReplace);
      toast({
        title: "Upload successful",
        description: `${res.inserted_transactions} tx · ${res.inserted_nav_snapshots} NAV snapshots`,
      });
      setNavBulkFile(null);
      setNavBulkRows([]);
      setNavBulkErrors([]);
      setNavBulkReplace(false);
      await reload();
    } catch (err) {
      const d = err.response?.data?.detail;
      const e = typeof d === "object" ? d.errors || [d.message] : [d || "Upload failed"];
      setNavBulkErrors(e);
      toast({ title: "Upload failed", description: e[0] });
    } finally {
      setNavBulkBusy(false);
    }
  };

  const downloadNavTemplate = () => {
    const csv = [
      "Date,NAV,Member,Type,Funds,Units",
      "2026-06-08,103.5641,Habiba Jetha,subscription,199750,1928.7572",
      "2026-06-08,103.5641,Moshin Chamadia,redemption,51782.05,500",
      "2026-04-01,100,Maheen Chamadiya,bonus,683.12,683.1234",
      "2026-06-05,105.0081,,daily,,",
      "2026-06-04,105.1295,,daily,,",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nav_history_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      {/* Bulk Cashflow Upload */}
      <div data-testid="bulk-upload-card" className="lg:col-span-5 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Upload size={16} className="text-amber-400" />
              <div className="text-sm font-semibold text-white">Bulk Upload Cashflow</div>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              CSV columns: <span className="text-slate-300 font-mono">Date, Member, Type, NAV, Units, Amount, AMC Fees</span>
            </div>
          </div>
          <button
            type="button"
            onClick={downloadTemplate}
            data-testid="download-template-btn"
            className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-800 hover:border-emerald-500/50"
          >
            <FileText size={12} /> Download CSV template
          </button>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-4 items-start">
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">CSV file</Label>
            <Input
              type="file"
              accept=".csv,text/csv"
              data-testid="bulk-csv-input"
              onChange={(e) => handleBulkFile(e.target.files?.[0] || null)}
              className="mt-1.5 bg-slate-950 border-slate-800 text-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-amber-500 file:text-slate-950 file:px-3 file:py-1.5 file:text-xs file:font-semibold hover:file:bg-amber-400 cursor-pointer"
            />
            {bulkFile && (
              <div className="text-[11px] text-slate-500 mt-2">
                <span className="text-slate-300">{bulkFile.name}</span> · {bulkRows.length} valid rows
                {bulkErrors.length > 0 && <span className="text-rose-400"> · {bulkErrors.length} errors</span>}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bulkReplaceAll}
                data-testid="bulk-replace-all-checkbox"
                onChange={(e) => setBulkReplaceAll(e.target.checked)}
                className="mt-0.5 accent-rose-500"
              />
              <div className="text-xs">
                <div className="text-slate-300">
                  Replace all existing transactions
                </div>
                <div className="text-[11px] text-slate-500">
                  If checked, deletes all current transactions before importing. Recommended for first-time setup.
                </div>
              </div>
            </label>
            <Button
              type="button"
              onClick={submitBulk}
              disabled={bulkBusy || bulkRows.length === 0 || bulkErrors.length > 0}
              data-testid="bulk-upload-submit-btn"
              className="w-fit h-10 px-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold disabled:opacity-50"
            >
              <Upload size={14} className="mr-2" />
              {bulkBusy ? "Uploading…" : `Upload ${bulkRows.length} transaction${bulkRows.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>

        {bulkErrors.length > 0 && (
          <div className="mt-4 bg-rose-500/10 border border-rose-500/30 rounded-md p-3" data-testid="bulk-errors">
            <div className="flex items-center gap-2 text-rose-300 text-xs font-semibold mb-1.5">
              <AlertCircle size={12} /> {bulkErrors.length} validation error{bulkErrors.length === 1 ? "" : "s"}
            </div>
            <ul className="text-[11px] text-rose-300/90 space-y-0.5 max-h-40 overflow-y-auto">
              {bulkErrors.slice(0, 20).map((e, i) => <li key={i}>· {e}</li>)}
              {bulkErrors.length > 20 && <li className="text-rose-400/70">…and {bulkErrors.length - 20} more</li>}
            </ul>
          </div>
        )}

        {bulkRows.length > 0 && bulkErrors.length === 0 && (
          <div className="mt-4 max-h-56 overflow-y-auto border border-slate-800 rounded-md" data-testid="bulk-preview">
            <table className="w-full text-xs">
              <thead className="bg-slate-950 text-slate-400 sticky top-0">
                <tr>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Member</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-right p-2">Funds</th>
                  <th className="text-right p-2">NAV</th>
                  <th className="text-right p-2">Units</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {bulkRows.slice(0, 50).map((r, i) => (
                  <tr key={i} className="text-slate-300">
                    <td className="p-2 font-mono">{r.date}</td>
                    <td className="p-2">{r.memberName}</td>
                    <td className={`p-2 capitalize ${r.type === "subscription" ? "text-emerald-400" : "text-rose-400"}`}>{r.type}</td>
                    <td className="p-2 text-right font-mono">{fmtINR(r.funds)}</td>
                    <td className="p-2 text-right font-mono">{r.nav.toFixed(4)}</td>
                    <td className="p-2 text-right font-mono">{r.units.toFixed(4)}</td>
                  </tr>
                ))}
                {bulkRows.length > 50 && (
                  <tr><td colSpan={6} className="p-2 text-center text-slate-500">…and {bulkRows.length - 50} more rows</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk NAV History Upload */}
      <div data-testid="bulk-nav-upload-card" className="lg:col-span-5 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Upload size={16} className="text-emerald-400" />
              <div className="text-sm font-semibold text-white">Bulk Upload NAV History</div>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              CSV columns: <span className="text-slate-300 font-mono">Date, NAV, Member, Type, Funds, Units</span>
              <br/>Type can be <span className="text-emerald-400">subscription</span> / <span className="text-rose-400">redemption</span> / <span className="text-amber-400">bonus</span> (member + funds/units required) or <span className="text-slate-300">daily</span> (just date + NAV).
            </div>
          </div>
          <button
            type="button"
            onClick={downloadNavTemplate}
            data-testid="download-nav-template-btn"
            className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-800 hover:border-emerald-500/50"
          >
            <FileText size={12} /> Download CSV template
          </button>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-4 items-start">
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">CSV file</Label>
            <Input
              type="file"
              accept=".csv,text/csv"
              data-testid="bulk-nav-csv-input"
              onChange={(e) => handleNavBulkFile(e.target.files?.[0] || null)}
              className="mt-1.5 bg-slate-950 border-slate-800 text-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-500 file:text-slate-950 file:px-3 file:py-1.5 file:text-xs file:font-semibold hover:file:bg-emerald-400 cursor-pointer"
            />
            {navBulkFile && (
              <div className="text-[11px] text-slate-500 mt-2">
                <span className="text-slate-300">{navBulkFile.name}</span> · {navBulkRows.length} valid rows
                {navBulkErrors.length > 0 && <span className="text-rose-400"> · {navBulkErrors.length} errors</span>}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={navBulkReplace}
                data-testid="bulk-nav-replace-checkbox"
                onChange={(e) => setNavBulkReplace(e.target.checked)}
                className="mt-0.5 accent-rose-500"
              />
              <div className="text-xs">
                <div className="text-slate-300">Replace all existing daily/live NAV snapshots</div>
                <div className="text-[11px] text-slate-500">Only wipes daily/live entries. Transactions are untouched.</div>
              </div>
            </label>
            <Button
              type="button"
              onClick={submitNavBulk}
              disabled={navBulkBusy || navBulkRows.length === 0 || navBulkErrors.length > 0}
              data-testid="bulk-nav-upload-submit-btn"
              className="w-fit h-10 px-5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold disabled:opacity-50"
            >
              <Upload size={14} className="mr-2" />
              {navBulkBusy ? "Uploading…" : `Upload ${navBulkRows.length} row${navBulkRows.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>

        {navBulkErrors.length > 0 && (
          <div className="mt-4 bg-rose-500/10 border border-rose-500/30 rounded-md p-3" data-testid="bulk-nav-errors">
            <div className="flex items-center gap-2 text-rose-300 text-xs font-semibold mb-1.5">
              <AlertCircle size={12} /> {navBulkErrors.length} validation error{navBulkErrors.length === 1 ? "" : "s"}
            </div>
            <ul className="text-[11px] text-rose-300/90 space-y-0.5 max-h-40 overflow-y-auto">
              {navBulkErrors.slice(0, 20).map((e, i) => <li key={i}>· {e}</li>)}
              {navBulkErrors.length > 20 && <li className="text-rose-400/70">…and {navBulkErrors.length - 20} more</li>}
            </ul>
          </div>
        )}

        {navBulkRows.length > 0 && navBulkErrors.length === 0 && (
          <div className="mt-4 max-h-56 overflow-y-auto border border-slate-800 rounded-md" data-testid="bulk-nav-preview">
            <table className="w-full text-xs">
              <thead className="bg-slate-950 text-slate-400 sticky top-0">
                <tr>
                  <th className="text-left p-2">Date</th>
                  <th className="text-right p-2">NAV</th>
                  <th className="text-left p-2">Member</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-right p-2">Funds</th>
                  <th className="text-right p-2">Units</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {navBulkRows.slice(0, 50).map((r, i) => (
                  <tr key={i} className="text-slate-300">
                    <td className="p-2 font-mono">{r.date}</td>
                    <td className="p-2 text-right font-mono">{r.nav.toFixed(4)}</td>
                    <td className="p-2">{r.memberName || <span className="text-slate-600">—</span>}</td>
                    <td className={`p-2 capitalize ${
                      r.type === "subscription" ? "text-emerald-400" :
                      r.type === "redemption" ? "text-rose-400" :
                      r.type === "bonus" ? "text-amber-400" : "text-slate-400"
                    }`}>{r.type}</td>
                    <td className="p-2 text-right font-mono">{r.funds > 0 ? fmtINR(r.funds) : "—"}</td>
                    <td className="p-2 text-right font-mono">{r.units > 0 ? r.units.toFixed(4) : "—"}</td>
                  </tr>
                ))}
                {navBulkRows.length > 50 && (
                  <tr><td colSpan={6} className="p-2 text-center text-slate-500">…and {navBulkRows.length - 50} more rows</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6">
        <div className="text-sm font-semibold text-white">New Transaction</div>
        <div className="text-xs text-slate-500">
          Format: Date · Name · Details · Units Cr · Funds Cr · Units Dr · Funds Dr
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Type</Label>
            <div className="mt-2 inline-flex rounded-md border border-slate-800 overflow-hidden">
              <button type="button" onClick={() => updateNew("type", "subscription")}
                data-testid="tx-type-subscription"
                className={`px-4 py-2 text-xs ${form.type === "subscription" ? "bg-emerald-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
                Subscription (Credit)
              </button>
              <button type="button" onClick={() => updateNew("type", "bonus")}
                data-testid="tx-type-bonus"
                className={`px-4 py-2 text-xs border-l border-slate-800 ${form.type === "bonus" ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
                Bonus
              </button>
              <button type="button" onClick={() => updateNew("type", "redemption")}
                data-testid="tx-type-redemption"
                className={`px-4 py-2 text-xs border-l border-slate-800 ${form.type === "redemption" ? "bg-rose-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:text-white"}`}>
                Redemption (Debit)
              </button>
            </div>
            {form.type === "bonus" && (
              <div className="mt-2 text-[11px] text-amber-400/80">
                Free units issued — no cash inflow, no AMC fee. Bonus is tracked separately from capital invested.
              </div>
            )}
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => updateNew("date", e.target.value)}
              className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Member</Label>
            <Select value={form.memberId} onValueChange={(v) => updateNew("memberId", v)}>
              <SelectTrigger className="mt-1.5 bg-slate-950 border-slate-800 text-white focus:ring-emerald-500">
                <SelectValue placeholder="Select member" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-white max-h-[320px]">
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">
              {form.type === "bonus"
                ? "Bonus Capital ₹"
                : `Gross Funds (${form.type === "subscription" ? "Cr" : "Dr"}) ₹`}
            </Label>
            <Input type="number" step="0.01" value={form.funds}
              onChange={(e) => updateNew("funds", e.target.value)}
              placeholder={
                form.type === "bonus"
                  ? "Notional bonus value (e.g. 50000)"
                  : form.type === "redemption"
                    ? "e.g. 100000 (cash paid to member)"
                    : "e.g. 100000 (before AMC fee)"
              }
              className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">
              {form.type === "bonus" ? "Bonus NAV ₹" : "NAV at issuance ₹"}
            </Label>
            <Input type="number" step="0.0001" value={form.nav}
              onChange={(e) => updateNew("nav", e.target.value)}
              placeholder={form.type === "bonus" ? "e.g. 100" : "e.g. 105.15"}
              className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500" />
          </div>

          {form.type !== "bonus" && (
            <div className="md:col-span-2" data-testid="amc-fee-controls">
              <Label className="text-slate-300 text-xs uppercase tracking-wider">AMC Fee</Label>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.amcEnabled}
                    onChange={(e) => updateNew("amcEnabled", e.target.checked)}
                    data-testid="tx-amc-enabled"
                    className="accent-emerald-500 w-4 h-4"
                  />
                  <span className="text-xs text-slate-200">Apply AMC fee</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500 uppercase tracking-wider">Rate</span>
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    value={form.amcRate}
                    onChange={(e) => updateNew("amcRate", e.target.value)}
                    disabled={!form.amcEnabled}
                    data-testid="tx-amc-rate"
                    className="h-8 w-24 bg-slate-900 border-slate-800 text-white focus-visible:ring-emerald-500 font-mono disabled:opacity-40"
                  />
                  <span className="text-xs text-slate-400 font-mono">%</span>
                </div>
                <div className="text-[11px] text-slate-500 ml-auto font-mono">
                  {form.amcEnabled
                    ? `${form.type === "redemption" ? "Added to" : "Deducted from"} gross funds`
                    : "No AMC fee will be applied"}
                </div>
              </div>
            </div>
          )}
          <div className="md:col-span-2">
            <Label className="text-slate-300 text-xs uppercase tracking-wider">
              {form.type === "bonus"
                ? "Bonus Units (auto-calculated)"
                : `Units (${form.type === "subscription" ? "Cr" : "Dr"}) — auto-calculated`}
            </Label>
            <Input type="number" step="0.0001" value={form.units}
              onChange={(e) => updateNew("units", e.target.value)}
              placeholder="Funds ÷ NAV"
              className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500 font-mono" />
            {form._amcFee && form.type !== "bonus" && (
              <div className="mt-2 text-[11px] text-slate-400 bg-slate-950 border border-slate-800 rounded-md p-2.5 font-mono" data-testid="amc-fee-breakdown">
                <div className="flex justify-between">
                  <span>{form.type === "redemption" ? "Gross payout to member:" : "Gross funds:"}</span>
                  <span className="text-slate-200">{fmtINR(parseFloat(form.funds) || 0)}</span>
                </div>
                {form.amcEnabled && parseFloat(form.amcRate) > 0 ? (
                  <div className="flex justify-between">
                    <span>{form.type === "redemption" ? "+" : "−"} AMC fee ({form.amcRate}%):</span>
                    <span className={form.type === "redemption" ? "text-amber-300" : "text-rose-400"}>
                      {form.type === "redemption" ? "+" : "−"}{fmtINR(parseFloat(form._amcFee) || 0)}
                    </span>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-slate-500">AMC fee:</span>
                    <span className="text-slate-500 italic">Not applied</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-800 pt-1 mt-1">
                  <span>{form.type === "redemption" ? "Units redeemed (Dr):" : "Units issued (Cr):"}</span>
                  <span className="text-emerald-400">{fmtINR(parseFloat(form._netFunds) || 0)} ÷ ₹{form.nav} = {form.units}</span>
                </div>
              </div>
            )}
            {form.type === "bonus" && form.funds && form.nav && (
              <div className="mt-2 text-[11px] text-slate-400 bg-amber-500/5 border border-amber-500/20 rounded-md p-2.5 font-mono" data-testid="bonus-breakdown">
                <div className="flex justify-between">
                  <span className="text-amber-300">Bonus Capital:</span>
                  <span className="text-slate-200">{fmtINR(parseFloat(form.funds) || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-300">Bonus Units = Funds ÷ NAV:</span>
                  <span className="text-emerald-400">{fmtINR(parseFloat(form.funds) || 0)} ÷ ₹{form.nav} = {form.units}</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Tracked separately — does not affect Capital Invested or Avg NAV.
                </div>
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <Label className="text-slate-300 text-xs uppercase tracking-wider">Details / Notes</Label>
            <Textarea value={form.details} onChange={(e) => updateNew("details", e.target.value)}
              placeholder="e.g. Q2 top-up, SIP…" rows={2}
              className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500" />
          </div>

          {/* Auto-journal leg selector — only for sub/redeem (bonus skipped on backend) */}
          {form.type !== "bonus" && (
            <div className="md:col-span-2" data-testid="journal-legs-panel">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-slate-300 text-xs uppercase tracking-wider">
                  Auto-journal legs
                </Label>
                <div className="flex items-center gap-2 text-[11px]">
                  <button
                    type="button"
                    onClick={() => updateNew("journalLegs", ALL_LEG_KEYS)}
                    data-testid="journal-legs-all"
                    className="px-2 py-0.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-emerald-400"
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => updateNew("journalLegs", [])}
                    data-testid="journal-legs-none"
                    className="px-2 py-0.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-rose-400"
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="bg-slate-950 border border-slate-800 rounded-md divide-y divide-slate-800">
                {JOURNAL_LEGS.map((leg) => {
                  const isRedemption = form.type === "redemption";
                  const label = isRedemption ? leg.red : leg.sub;
                  const fundsNum = parseFloat(form.funds) || 0;
                  const amcNum = (form.amcEnabled && parseFloat(form.amcRate) > 0)
                    ? fundsNum * (parseFloat(form.amcRate) / 100)
                    : 0;
                  const grossNum = isRedemption ? fundsNum : fundsNum + amcNum; // bank uses gross
                  const unitsNum = parseFloat(form.units) || 0;
                  let amtPreview = "";
                  let disabled = false;
                  let disabledReason = "";
                  if (leg.scale === "amount") amtPreview = fundsNum > 0 ? fmtINR(isRedemption ? fundsNum : (fundsNum - amcNum)) : "—";
                  else if (leg.scale === "gross") amtPreview = grossNum > 0 ? fmtINR(grossNum) : "—";
                  else if (leg.scale === "units") amtPreview = unitsNum > 0 ? `${fmtUnitsPlain(unitsNum)} u` : "—";
                  else if (leg.scale === "amc") {
                    amtPreview = amcNum > 0 ? fmtINR(amcNum) : "—";
                    if (amcNum <= 0) {
                      disabled = true;
                      disabledReason = "AMC fee is 0 — leg won't post";
                    }
                  }
                  const checked = (form.journalLegs || []).includes(leg.key) && !disabled;
                  return (
                    <label
                      key={leg.key}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-900 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                      title={disabledReason || "Click to toggle this leg"}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        data-testid={`journal-leg-${leg.key}`}
                        onChange={(e) => {
                          const cur = new Set(form.journalLegs || []);
                          if (e.target.checked) cur.add(leg.key);
                          else cur.delete(leg.key);
                          updateNew("journalLegs", Array.from(cur));
                        }}
                        className="accent-emerald-500 w-4 h-4"
                      />
                      <div className="flex-1 flex items-center justify-between text-xs">
                        <span className={label.startsWith("Dr") ? "text-emerald-400 font-mono" : "text-rose-400 font-mono"}>
                          {label}
                        </span>
                        <span className="text-slate-400 font-mono">{amtPreview}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5">
                {(form.journalLegs || []).length === 0
                  ? "No legs selected — nothing will post to the ledger."
                  : `${(form.journalLegs || []).length} of 7 leg${(form.journalLegs || []).length === 1 ? "" : "s"} will post to the ledger on save.`}
              </div>
            </div>
          )}
          {form.type === "bonus" && (
            <div className="md:col-span-2 text-[11px] text-amber-400/70 bg-amber-500/5 border border-amber-500/20 rounded-md p-2.5">
              Bonus transactions do not trigger auto-journals. They are recorded against bonus units only.
            </div>
          )}
        </div>

        <Button type="submit" disabled={busy}
          className="mt-5 h-11 px-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold disabled:opacity-60">
          <Check className="mr-2" size={16} /> {busy ? "Recording…" : "Record transaction"}
        </Button>
      </form>

      {/* Recent transactions */}
      <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-white">All Transactions</div>
            <div className="text-xs text-slate-500">{transactions.length} total</div>
          </div>
        </div>
        <div className="divide-y divide-slate-800 -mx-2 max-h-[600px] overflow-y-auto">
          {transactions.length === 0 && (
            <div className="text-xs text-slate-500 text-center py-8">No transactions yet.</div>
          )}
          {transactions.map((t) => {
            const isBonus = t.type === "bonus";
            const isSub = !isBonus && (t.fundsCr || 0) > 0;
            const amount = (t.fundsCr || 0) > 0 ? t.fundsCr : t.fundsDr;
            const units = (t.unitsCr || 0) > 0 ? t.unitsCr : t.unitsDr;
            const dotColor = isBonus ? "bg-amber-400" : isSub ? "bg-emerald-400" : "bg-rose-400";
            const amountColor = isBonus ? "text-amber-400" : isSub ? "text-emerald-400" : "text-rose-400";
            const sign = isBonus ? "★ " : (isSub ? "+" : "-");
            return (
              <div key={t.id} className="px-2 py-3 group">
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${dotColor}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm text-white font-medium truncate">
                        {t.name}
                        {isBonus && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 uppercase tracking-wider">Bonus</span>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => startEdit(t)}
                          className="text-slate-500 hover:text-emerald-400 p-1"
                          title="Edit">
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => deleteTx(t)}
                          className="text-slate-500 hover:text-rose-400 p-1"
                          title="Delete">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {longDate(t.date)} · NAV ₹{t.nav.toFixed(4)}
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className={`text-xs ${amountColor}`}>
                        {sign}{fmtINR(amount)}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono">{fmtUnits(units)} u</div>
                    </div>
                    {t.details && (
                      <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{t.details}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit transaction</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="text-xs text-slate-400">
                Member: <span className="text-white">{editing.memberName}</span>
              </div>

              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Type</Label>
                <div className="mt-2 inline-flex rounded-md border border-slate-800 overflow-hidden">
                  <button onClick={() => updateEdit("type", "subscription")}
                    className={`px-4 py-2 text-xs ${editing.type === "subscription" ? "bg-emerald-500 text-slate-950" : "bg-slate-900 text-slate-400"}`}>
                    Subscription
                  </button>
                  <button onClick={() => updateEdit("type", "bonus")}
                    className={`px-4 py-2 text-xs border-l border-slate-800 ${editing.type === "bonus" ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-400"}`}>
                    Bonus
                  </button>
                  <button onClick={() => updateEdit("type", "redemption")}
                    className={`px-4 py-2 text-xs border-l border-slate-800 ${editing.type === "redemption" ? "bg-rose-500 text-slate-950" : "bg-slate-900 text-slate-400"}`}>
                    Redemption
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Date</Label>
                  <Input type="date" value={editing.date}
                    onChange={(e) => updateEdit("date", e.target.value)}
                    className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">NAV ₹</Label>
                  <Input type="number" step="0.0001" value={editing.nav}
                    onChange={(e) => updateEdit("nav", e.target.value)}
                    className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Funds ₹</Label>
                  <Input type="number" step="0.01" value={editing.funds}
                    onChange={(e) => updateEdit("funds", e.target.value)}
                    className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Units</Label>
                  <Input type="number" step="0.0001" value={editing.units}
                    onChange={(e) => updateEdit("units", e.target.value)}
                    className="mt-1.5 bg-slate-950 border-slate-800 text-white font-mono" />
                </div>
              </div>

              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Details</Label>
                <Textarea value={editing.details}
                  onChange={(e) => updateEdit("details", e.target.value)}
                  rows={2}
                  className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}
              className="text-slate-400 hover:text-white hover:bg-slate-800">
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={busy}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
